import type { Newsletter, NewsletterSections } from '@/types';
import { SECTION_LABELS, SECTION_ORDER } from '@/types';

const MAX_RESULTS = 8;
const SNIPPET_RADIUS = 40;

export type SearchMatchedField = 'title' | 'issueNumber' | keyof NewsletterSections;

export interface NewsletterSearchMatch {
  newsletter: Newsletter;
  matchedField: SearchMatchedField;
  snippet?: string;
  sectionLabel?: string;
}

function buildSnippet(text: string, matchIndex: number, matchLength: number): string {
  const start = Math.max(0, matchIndex - SNIPPET_RADIUS);
  const end = Math.min(text.length, matchIndex + matchLength + SNIPPET_RADIUS);
  const prefix = start > 0 ? '…' : '';
  const suffix = end < text.length ? '…' : '';
  return `${prefix}${text.slice(start, end).trim()}${suffix}`;
}

function matchNewsletter(newsletter: Newsletter, normalizedQuery: string): NewsletterSearchMatch | null {
  if (newsletter.title.toLowerCase().includes(normalizedQuery)) {
    return { newsletter, matchedField: 'title' };
  }

  const issueQuery = normalizedQuery.startsWith('#') ? normalizedQuery.slice(1) : normalizedQuery;
  if (issueQuery && String(newsletter.issueNumber).includes(issueQuery)) {
    return { newsletter, matchedField: 'issueNumber' };
  }

  for (const section of SECTION_ORDER) {
    const text = newsletter.sections[section];
    const index = text.toLowerCase().indexOf(normalizedQuery);
    if (index !== -1) {
      return {
        newsletter,
        matchedField: section,
        snippet: buildSnippet(text, index, normalizedQuery.length),
        sectionLabel: SECTION_LABELS[section],
      };
    }
  }

  return null;
}

/** Case-insensitive search across a newsletter's title, issue number, and all section content. */
export function searchNewsletters(newsletters: Newsletter[], query: string): NewsletterSearchMatch[] {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return [];

  const results: NewsletterSearchMatch[] = [];
  for (const newsletter of newsletters) {
    const match = matchNewsletter(newsletter, normalizedQuery);
    if (match) results.push(match);
    if (results.length >= MAX_RESULTS) break;
  }
  return results;
}
